import {
  AccordionItemBodyProps,
  AccordionItemProps,
  AccordionItemTriggerProps,
  AccordionRootProps,
  Accordion as ChakraAccordion,
} from '@chakra-ui/react'
import { ReactNode, forwardRef } from 'react'
import { useTheme } from 'utils'
import { Text } from '.'

export interface Props extends Omit<AccordionRootProps, 'children'> {
  contentProps?: Omit<AccordionItemBodyProps, 'children'>
  buttonProps?: Omit<AccordionItemTriggerProps, 'children'>
  isFlex?: boolean
  items: AccordionItem[]
}

export interface AccordionItem extends Omit<AccordionItemProps, 'content' | 'value' | 'title'> {
  content: ReactNode
  forceScroll?: boolean
  title: ReactNode
  value?: string
}

export default forwardRef<HTMLDivElement, Props>(
  ({ contentProps, buttonProps, isFlex, items, ...rest }, ref) => {
    const { theme } = useTheme()

    return (
      <ChakraAccordion.Root
        ref={ref}
        borderRadius={8}
        collapsible
        {...(isFlex && { display: 'flex', flexDirection: 'column', overflow: 'auto' })}
        {...rest}
      >
        {items.map(({ title, content, forceScroll, value }, index) => (
          <ChakraAccordion.Item
            key={value ?? index}
            value={value ?? index.toString()}
            borderTopWidth="none"
            borderBottomWidth={
              index === items.length - 1
                ? '0'
                : theme === 'light'
                ? '1px solid #DEDEDE'
                : '1px solid rgba(255, 255, 255, 0.1)'
            }
          >
            <ChakraAccordion.ItemTrigger
              {...(isFlex && { flexShrink: 0 })}
              cursor="pointer"
              px={4}
              justifyContent="space-between"
              {...buttonProps}
            >
              {typeof title === 'string' ? (
                <Text textStyle={buttonProps?.textStyle || 'bodyLarge'}>{title}</Text>
              ) : (
                title
              )}
              <ChakraAccordion.ItemIndicator />
            </ChakraAccordion.ItemTrigger>
            <ChakraAccordion.ItemContent
              {...(isFlex && { display: 'flex', overflowY: 'auto', flex: 1 })}
            >
              <ChakraAccordion.ItemBody
                px={0}
                {...(forceScroll && {
                  overflowY: 'scroll',
                  css: { scrollbarColor: 'grey transparent', ...contentProps?.css },
                })}
                {...contentProps}
              >
                {content}
              </ChakraAccordion.ItemBody>
            </ChakraAccordion.ItemContent>
          </ChakraAccordion.Item>
        ))}
      </ChakraAccordion.Root>
    )
  }
)
